/**
 * @fileoverview Componente Atom para renderizar el ícono correspondiente a un evento de actividad de cliente en Inbox.
 * Utiliza memoización avanzada y mapeo de tipo de evento a SVG o sprite.
 *
 * @module ActivityIconMC
 * @category Component - Atom
 * @since 1.0.0
 * @version 1.0.0
 */
/* eslint-disable import/no-unresolved */
import React, { memo, useMemo } from 'react';

import AssignAgent from 'images/new_design/assign_agent.svg';
import CreateDeal from 'images/new_design/create_deal.svg';
import CreateNote from 'images/new_design/create_note.svg';
import CreateSale from 'images/new_design/create_sale.svg';
import ActiveBot from 'images/new_design/active_bot.svg';
import ReminderIcon from 'images/new_design/create_reminder.svg';
import OtherEvent from 'images/new_design/other_event.svg';
import AssignToList from 'images/new_design/assign_to_list.svg';
import ChangeStatusChat from 'images/new_design/change_status_chat.svg';
import UnreadChat from 'images/new_design/unread_chat.svg';
import CreateOrderNote from 'images/new_design/create_order_note.svg';
import LinkedinMessage from 'images/new_design/linkedin_message.svg';
import Call from 'images/new_design/call.svg';
import AnotherActivity from 'images/new_design/another_activity.svg';
import Meet from 'images/new_design/meet.svg';
import IconMC from '../../../components/IconMC';

/**
 * Componente Atom para mostrar el ícono de actividad según el tipo de evento.
 *
 * @component ActivityIconMC
 * @category Component - Atom
 *
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.event - Evento de actividad (debe tener type_event)
 * @param {string} [props.marginRule='mr-0'] - Clase de margen opcional para el ícono
 *
 * @returns {JSX.Element} Ícono SVG o sprite correspondiente al evento
 *
 * @example
 * <ActivityIconMC event={{ type_event: 'assign_agent' }} />
 *
 * @see {@link IconMC} Para íconos tipo sprite
 * @since 1.0.0
 * @version 1.0.0
 */
const ActivityIconMC = memo(({ event, marginRule = 'mr-0' }) => {
  const { img, namesSprite } = useMemo(() => {
    switch (event.type_event) {
      case 'assign_agent':
      case 'not_assign':
      case 'create_customer':
        return { img: AssignAgent, namesSprite: null };
      case 'create_deal':
      case 'change_stage_deal':
        return { img: CreateDeal, namesSprite: null };
      case 'create_note':
        return { img: CreateNote, namesSprite: null };
      case 'create_order_note':
        return { img: CreateOrderNote, namesSprite: null };
      case 'create_sale':
        return { img: CreateSale, namesSprite: null };
      case 'active_bot':
      case 'inactive_bot':
        return { img: ActiveBot, namesSprite: null };
      case 'create_reminder':
        return { img: ReminderIcon, namesSprite: null };
      case 'assign_to_list':
        return { img: AssignToList, namesSprite: null };
      case 'change_status_chat':
        return { img: ChangeStatusChat, namesSprite: null };
      case 'unread_chat':
        return { img: UnreadChat, namesSprite: null };
      case 'linkedin_message':
        return { img: LinkedinMessage, namesSprite: null };
      case 'call':
        return { img: Call, namesSprite: null };
      case 'another_activity':
        return { img: AnotherActivity, namesSprite: null };
      case 'meet':
        return { img: Meet, namesSprite: null };
      case 'chatbot_activated':
      case 'chatbot_deactivated':
      case 'chatbot_insufficient_balance':
        return { img: null, namesSprite: 'new-bot' };
      default:
        return { img: OtherEvent, namesSprite: null };
    }
  }, [event.type_event]);

  if (namesSprite) {
    return <IconMC name={namesSprite} size="lg" />;
  }
  return <img src={img} className={`w-30 ${marginRule}`} alt="activity-icon" />;
});

ActivityIconMC.displayName = 'ActivityIconMC';

export default ActivityIconMC;
